/**
 * Cost intents: the cost of doing nothing, an incident's real cost, the
 * 18-month ledger, what a job will cost, and "is air freight worth A$X?".
 *
 * The regex engine sent every question carrying `$`, `crew` or `cost` to the
 * ledger table. Here the ledger needs a cost word (bare `$` and bare `crew`
 * no longer count); a question with an A$ amount and "worth" is a decision;
 * "what will X cost" on one machine is a job cost.
 */

import { BY_ID, MINES, RATES } from '../../../data/core.ts';
import type { MineCode } from '../../../schema/machine.ts';
import { coi, fmt$, fmtk, groupNum, INC_PLANNED, incidentCosts } from '../../oem-engine.ts';
import { ALERTS } from '../../seed/alerts.ts';
import { INCIDENTS } from '../../seed/incidents.ts';
import { loadOemDomain } from '../../useOemData.ts';
import { route, scopeLabel } from '../answer-kit.ts';
import { money } from '../stats.ts';
import { TOOLS, loadWorkOrders, type CostGroupBy } from '../tools.ts';
import { cues, type IntentDef } from './types.ts';

const COST_WORD = /\bcost|expensive|spend|per hour|cheap|\bprice|money|dollars?\b/;

// ---------------------------------------------------------------------------
// cost_of_inaction — demo L4062
// ---------------------------------------------------------------------------

export const costOfInaction: IntentDef = {
  id: 'cost_of_inaction',
  family: 'cost',
  priority: 88,
  examples: ['What does doing nothing cost us today?'],
  score: cues({
    any: [
      /cost of (doing nothing|waiting|inaction)|what does waiting cost|doing nothing cost|issues now|what.s wrong/,
      /cost (us|of) (if we|to) (wait|do nothing)|if we do nothing/,
    ],
    base: 5,
  }),
  async answer({ kit }) {
    kit.ran('cost_of_inaction');
    const scored = ALERTS.map((a) => ({ a, c: coi(a) })).filter(
      (x): x is { a: (typeof ALERTS)[number]; c: NonNullable<ReturnType<typeof coi>> } => x.c != null,
    );
    const sum = scored.reduce((s, x) => s + x.c.delta, 0);
    const top = scored.slice().sort((x, y) => y.c.delta - x.c.delta)[0];
    const markdown =
      `Across the **${ALERTS.length} open alerts** the cost of doing nothing is **${fmtk(sum)}** — ` +
      `the planned job against the same failure allowed to happen, with downtime priced from each site's ` +
      `lost-production rate. The biggest single exposure is **${top.a.machine}** (${top.a.comp}): ` +
      `${fmt$(top.c.planned)} planned against ${fmt$(top.c.unplanned)} if it fails first — ` +
      `${top.c.hU} h down at A$${groupNum(top.c.lost)}/h. ` +
      `The two incidents this fortnight are the evidence: S-05 cost A$24,300 against A$4,600 planned, ` +
      `L-18 A$98,900 against A$4,100.`;
    kit.focus({ machine: top.a.machine, component: top.a.comp, alert: top.a.id });
    return kit.done(markdown, ['issues now', 'rate card', 'work_orders.csv'], {
      navigate: route('issues'),
    });
  },
};

// ---------------------------------------------------------------------------
// incident_cost — port addition 8b
// ---------------------------------------------------------------------------

export const incidentCost: IntentDef = {
  id: 'incident_cost',
  family: 'cost',
  priority: 87,
  examples: ['What did the L-02 failure cost versus a planned change?', 'What were the precursors to the L-02 failure?'],
  score: cues({
    any: [
      /(cost|costs).{0,24}(versus|vs\.?|against).{0,24}(planned|preventive)/,
      /precursors? to /,
      /what did the .{0,20}(failure|incident|fire) cost/,
    ],
    base: 5,
  }),
  async answer({ kit, E, page }) {
    const byId = E.incidentIds[0] ? INCIDENTS.find((i) => i.id === E.incidentIds[0]) : undefined;
    const onPage = !E.machine && page.incident ? INCIDENTS.find((i) => i.id === page.incident) : undefined;
    const inc =
      byId ?? onPage ?? (E.machine ? INCIDENTS.find((i) => i.machine === E.machine) : INCIDENTS[0]);
    if (!inc) {
      const markdown =
        `${E.machine ?? 'That machine'} has no incident on record this fortnight — the two are ` +
        `${INCIDENTS.map((i) => `${i.machine} (${i.title.toLowerCase()})`).join(' and ')}. ` +
        `Ask me what the job on ${E.machine ?? 'it'} would cost instead.`;
      return kit.done(markdown, ['incidents']);
    }
    kit.ran('incident_costs', { incident: inc.id });
    const workOrders = await loadWorkOrders();
    const c = incidentCosts(inc, { workOrders });
    const planned = INC_PLANNED[inc.id];
    const allIn = c.allIn ?? 0;
    const w = c.workOrder;
    const precursors = inc.happened
      .slice(0, 3)
      .map((h) => `**${h.when}** — ${h.text.split('. ')[0]}.`)
      .join(' ');
    const markdown =
      `**${inc.machine} · ${inc.title}** — ${MINES[inc.mine as MineCode].name}, ${inc.date}.\n\n` +
      `The failure cost **${fmt$(allIn)}** all-in (${inc.workOrder}: ${fmt$(
        w?.total_direct_cost_aud ?? 0,
      )} direct plus ${fmt$(w?.downtime_cost_aud ?? 0)} of lost production over ${
        w?.downtime_h ?? 0
      } h down). The planned ${inc.comp.toLowerCase()} job against the same failure is ` +
      `**${fmt$(planned)}** — a difference of **${fmt$(allIn - planned)}**, ${(
        allIn / Math.max(1, planned)
      ).toFixed(1)}× the cost of doing it on schedule.\n\n` +
      `Precursors, in order: ${precursors} ` +
      `The agent chain replays the same fortnight on the shift-end export alone — open the incident to step through it.`;
    kit.focus({ machine: inc.machine, component: inc.comp, mine: inc.mine as MineCode });
    return kit.done(markdown, ['incidents', 'work_orders.csv', 'rate card'], {
      navigate: route('incident', inc.id),
    });
  },
};

// ---------------------------------------------------------------------------
// cost_ledger — demo L4075
// ---------------------------------------------------------------------------

export const costLedger: IntentDef = {
  id: 'cost_ledger',
  family: 'cost',
  priority: 64,
  examples: [
    'Compare cost per operating hour by mine and explain the difference',
    'Break down cost by crew at Kalbarri',
    'Which model is cheapest to run per hour?',
  ],
  score: cues({
    any: [COST_WORD],
    boost: [
      [/per (operating )?hour|cost per|by (mine|crew|class|model|machine)|ledger|compare/, 1],
      [/\bcrew\b/, 0.5],
    ],
    penalty: [
      [/\bworth\b|justif/, 3],
      [/what will .* cost|how much will .* cost/, 2],
    ],
    // One named machine is that machine's history, not the ledger.
    bonus: (E, ctx) =>
      E.machineFrom === 'text' && !/\bby (mine|crew|class|model|machine)|compare|which (mine|crew|model)/.test(ctx.L) ? -2 : 0,
  }),
  async answer({ kit, E, L }) {
    const g: CostGroupBy = /crew/.test(L)
      ? 'crew'
      : /class|type/.test(L)
        ? 'class'
        : /model/.test(L)
          ? 'model'
          : /machine/.test(L) && !E.mine
            ? 'machine'
            : 'mine';
    const mine = E.mine ?? null;
    const c = await kit.run(TOOLS.cost_summary, { group_by: g, mine });
    const markdown =
      `**Cost by ${g}${mine ? ` · ${MINES[mine].name}` : ''}** (18 months, all-in incl. lost production)\n\n` +
      `| ${g} | Total | Labour | Parts | Freight | Lost production |\n|---|---|---|---|---|---|\n` +
      c.rows
        .slice(0, 8)
        .map(
          (r) =>
            `| ${r.group} | ${money(r.total)} | ${money((r.Labour as number) || 0)} | ` +
            `${money((r.Parts as number) || 0)} | ${money((r.Freight as number) || 0)} | ` +
            `${money((r['Lost production'] as number) || 0)} |`,
        )
        .join('\n') +
      `\n\nCost per operating hour: Kalbarri A$${c.avg_cost_per_op_hour_by_mine.KG}, ` +
      `Broken Ridge A$${c.avg_cost_per_op_hour_by_mine.BR}, Mount Isolde A$${c.avg_cost_per_op_hour_by_mine.MI}` +
      (mine && !['KG', 'BR', 'MI'].includes(mine)
        ? `, ${MINES[mine].name} A$${c.avg_cost_per_op_hour_by_mine[mine]}`
        : '') +
      `. ` +
      `Kalbarri is highest despite the cheapest labour ($${RATES.KG.labour}/h) and easiest ground because reactive ` +
      `downtime is priced at A$${RATES.KG.lost.lhd}/h for an LHD and its breakdowns run ~9 h. ` +
      `Most expensive per hour: ${c.top_cost_per_op_hour
        .slice(0, 3)
        .map((x) => `${x.machine} (${x.mine}, A$${x.aud_per_op_h})`)
        .join(', ')}. ${c.note}`;
    if (mine) kit.focus({ mine });
    return kit.done(markdown, ['cost_summary', 'cost_ledger', 'rate_card']);
  },
};

// ---------------------------------------------------------------------------
// job_cost — new: "what will the S-04 hammer exchange cost?"
// ---------------------------------------------------------------------------

export const jobCost: IntentDef = {
  id: 'job_cost',
  family: 'cost',
  priority: 80,
  examples: ['What will the S-04 hammer exchange cost?', 'How much will the T-09 brake job cost?'],
  needs: ['machine'],
  score: cues({
    any: [
      /what (will|would|does|did) .{0,40}\bcost\b/,
      /how much (will|would|does|is) .{0,40}\b(cost|be)\b/,
      /cost (to|of) (fix|change|replace|repair|do|the job|the exchange|the change)/,
      /(job|exchange|repair|change-?out) cost|quote for/,
      /what would that cost|what will that cost/,
    ],
    not: [/cost of (doing nothing|waiting|inaction)|doing nothing cost|what does waiting cost/, /versus|vs\.?|against/, /per (operating )?hour/],
    base: 5,
  }),
  async answer({ kit, E }) {
    const mid = E.machine as string;
    const comp = E.components[0];
    const j = await kit.run(TOOLS.job_cost, { machine_id: mid, component: comp ? comp.word : null });
    const where = scopeLabel(E).slice(3);
    const markdown =
      `**${where} · ${j.component}** — planned job **${fmt$(j.planned)}**` +
      (j.basis === 'alert'
        ? ` (${j.alert}: ${j.plan_note}).`
        : j.basis === 'draft'
          ? ` — the agent-drafted ${j.draft?.wo}: ${j.draft?.labour_h} h labour, parts ${j.draft?.parts || '—'}, ${j.draft?.crew}.`
          : ` — estimated from catalogue parts and the fleet's mean labour hours at A$${j.labour_rate}/h.`) +
      `\n\n` +
      (j.draft && j.basis !== 'draft'
        ? `- Drafted order ${j.draft.wo}: ${fmt$(j.draft.direct)} direct, ${j.draft.labour_h} h, ${j.draft.crew}.\n`
        : '') +
      (j.unplanned != null
        ? `- If it fails first: **${fmt$(j.unplanned)}** — ${j.failure_note} (${j.downtime_h_unplanned} h at A$${groupNum(j.lost_rate)}/h).\n`
        : '') +
      (j.exchange ? `- Exchange unit ${j.exchange.part_no}: ${j.exchange.note}.\n` : '') +
      (j.parts.length
        ? `- Parts: ${j.parts.map((p) => `${p.part} A$${groupNum(p.list_price_aud)} (${p.lead_time_days} d)`).join('; ')}.\n`
        : '') +
      (j.orders.length ? `- Orders: ${j.orders.join('; ')}.` : '');
    kit.focus({ machine: mid, component: j.component, mine: j.mine, alert: j.alert ?? undefined });
    kit.remember({ component: j.component });
    return kit.done(markdown.trim(), ['job_cost', 'work_orders.csv', 'rate card', 'parts_catalogue.csv']);
  },
};

// ---------------------------------------------------------------------------
// freight_decision — new: "is air freight for the J-04 kit worth A$520?"
// ---------------------------------------------------------------------------

export const freightDecision: IntentDef = {
  id: 'freight_decision',
  family: 'cost',
  priority: 84,
  examples: ['Is air freight for the J-04 kit worth A$520?', 'Should we pay for air on the S-09 motor?'],
  needs: ['machine'],
  score: cues({
    any: [/\bworth\b|justif|pay for (the )?(air|freight|express)|air freight|air vs road|fly (it|the)/],
    boost: [[/air|freight|express/, 1]],
    bonus: (E) => (E.amount ? 1.5 : 0),
    base: 5,
  }),
  async answer({ kit, E }) {
    const mid = E.machine as string;
    const f = BY_ID[mid];
    const [parts, j] = await Promise.all([
      loadOemDomain('parts'),
      kit.run(TOOLS.job_cost, { machine_id: mid, component: E.components[0]?.word ?? null }),
    ]);
    kit.ran('freight_options', { machine_id: mid });
    const order = parts.orders.find((o) => o.machine_id === mid && /Draft|Ordered/.test(o.status));
    const alert = ALERTS.find((a) => a.machine === mid);
    const freightLine = alert?.chain.find((s) => /air/i.test(s.output) && /road/i.test(s.output))?.output;
    const air = E.amount ?? Number(/\(\$?(\d[\d,]*)\)/.exec(freightLine ?? '')?.[1]?.replace(/,/g, '') ?? NaN);
    const airDays = Number(/air (\d+) ?d/i.exec(freightLine ?? '')?.[1] ?? NaN);
    const roadDays = Number(/road (\d+) ?d/i.exec(freightLine ?? '')?.[1] ?? RATES[f.mine].freight_days);
    const windowDays = /(\d+)[–-](\d+) days/.exec(alert?.window ?? '');
    const opens = windowDays ? Number(windowDays[1]) : null;
    const exposure = j.unplanned != null ? j.unplanned - j.planned : null;
    const conf = alert ? alert.conf : null;
    const expected = exposure != null && conf != null ? exposure * conf : null;
    const verdict =
      expected != null && Number.isFinite(air)
        ? expected > air * 5
          ? `**Yes.** A$${groupNum(air)} of freight against ${fmt$(expected)} of expected exposure (${fmt$(exposure ?? 0)} × ${Math.round((conf ?? 0) * 100)}% confidence) — ${Math.round(expected / air)}× cover.`
          : expected > air
            ? `**Probably.** The freight is covered ${Math.round(expected / air)}× by the expected exposure, but it is close enough to check the window.`
            : `**No.** The expected exposure (${fmt$(expected)}) does not cover A$${groupNum(air)} of freight — send it by road.`
        : `I can't price the exposure on ${mid} — there is no open alert with a planned/unplanned pair — so compare the freight against A$${groupNum(j.lost_rate)}/h of lost production.`;
    const timing =
      opens != null && Number.isFinite(roadDays)
        ? `Road takes ${roadDays} d${Number.isFinite(airDays) ? `, air ${airDays} d` : ''}; the failure window opens in ${opens} day${opens === 1 ? '' : 's'} (${alert?.window}), so ${
            roadDays >= opens ? '**road lands inside the window** — the kit could arrive after the failure.' : 'road still lands before the window opens.'
          }`
        : alert
          ? `Window: ${alert.window}.`
          : '';
    const markdown =
      `**Air freight for ${mid}${order ? ` · ${order.order_no} ${order.part_no}` : ''}**\n\n` +
      `${verdict}\n\n${timing}\n\n` +
      (freightLine ? `Parts & logistics agent: ${freightLine}\n\n` : '') +
      `Downtime at ${MINES[f.mine].name} is priced at A$${groupNum(j.lost_rate)}/h for the ${f.model}; ` +
      `approving the requisition is in Approvals.`;
    kit.focus({ machine: mid, component: j.component, mine: f.mine, alert: alert?.id });
    return kit.done(markdown, ['orders', 'alerts', 'rate card', 'job_cost'], { navigate: route('approvals') });
  },
};
